import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Article as PolicyIcon,
  Timeline as TimelineIcon,
  Link as ReferenceIcon,
  Visibility as ViewIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import apiService from '../services/api';
import { pageVariants, sectionVariants, staggerContainer, staggerItem } from '../animations/variants';
import { useReadOnlyMode } from '../hooks/useReadOnlyMode';
import { ReadOnlyButton } from '../components/common/ReadOnlyButton';
import PolicyGeneratorLink from '../components/common/PolicyGeneratorLink';
import { PolicyReferenceDetail } from '../components/policies/PolicyReferenceDetail';
import { getCustomerIdFromSubdomain } from '../utils/constants';
import { statusChipSx } from './portal/portalStyles';
import { formatPolicyJson } from '../utils/policyJson';

const CMP_BRAND_COLOR = '#9B59B6';
const CMP_BRAND_HOVER = '#864AA0';
const CMP_BRAND_TINT = '#F4EAF7';

// Reference rows are drawn in the portal's info (blue) triad so they never read
// as a variant of the purple Prism-managed brand: the two are different kinds of
// object, not two states of one.
const REF_BRAND_COLOR = '#4698D3';

// The table head style the page already used, lifted to a constant now that the
// table carries six columns.
const HEAD_CELL_SX = {
  fontFamily: 'Metropolis, sans-serif',
  fontWeight: 600,
  color: '#253E66',
  fontSize: '0.8rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const REFERENCE_OWNERSHIP_TEXT =
  'Customer-owned policy. Prism attaches it at its existing ARN and never creates, edits, versions or deletes it. The document can differ in every account.';

// Heuristic warning: if the policy name collides with a well-known AWS managed
// policy name, the user almost certainly meant to attach the AWS-managed
// version via the Permission Set's "AWS Managed Policies" section instead.
const AWS_MANAGED_NAMES = new Set([
  'AdministratorAccess',
  'PowerUserAccess',
  'ReadOnlyAccess',
  'IAMFullAccess',
  'AmazonS3ReadOnlyAccess',
  'AmazonS3FullAccess',
  'AmazonEC2ReadOnlyAccess',
  'AmazonEC2FullAccess',
  'Billing',
]);

const emptyDoc = '{\n  "Version": "2012-10-17",\n  "Statement": []\n}';

// Shared by the list and the reference-detail takeover so both sit on the same
// page canvas.
const PAGE_STYLE = {
  minHeight: '100vh',
  paddingTop: 32,
  paddingBottom: 32,
  paddingLeft: 32,
  paddingRight: 32,
  backgroundColor: '#F9FBFD',
};

export default function CustomerManagedPolicies() {
  const isReadOnly = useReadOnlyMode();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', policy_document: emptyDoc });
  const [editorError, setEditorError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [propagateConfirm, setPropagateConfirm] = useState(null); // { usage, body }

  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [deleteUsage, setDeleteUsage] = useState(null);
  const [deleteTyped, setDeleteTyped] = useState('');
  const [deleting, setDeleting] = useState(false);

  const [rolloutPanel, setRolloutPanel] = useState(null); // policy id whose rollouts are shown

  // --- Policy references (pointers to CUSTOMER-owned policies) ---
  const customerId = getCustomerIdFromSubdomain();
  const [references, setReferences] = useState([]);
  const [referencesLoading, setReferencesLoading] = useState(true);
  const [referencesError, setReferencesError] = useState(null);
  const [refDetailId, setRefDetailId] = useState(null); // the reference being inspected
  const [refCreateOpen, setRefCreateOpen] = useState(false);
  const [refForm, setRefForm] = useState({ name: '', path: '/' });
  const [refFormError, setRefFormError] = useState(null);
  const [refSaving, setRefSaving] = useState(false);
  const [refDeleteCandidate, setRefDeleteCandidate] = useState(null);
  const [refDeleting, setRefDeleting] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.listCustomerManagedPolicies({
        page: page + 1,
        pageSize,
        search,
      });
      setRows(res.policies || []);
      setTotal(res.total || 0);
    } catch (e) {
      setError(e?.message || 'Failed to load policies');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => {
    reload();
  }, [reload]);

  // References are returned unpaginated (one bare array for the whole realm),
  // so they are loaded once and filtered client-side against the same search
  // box. A failure gets its own visible alert rather than an empty list: an
  // admin who is shown only Prism-managed rows would conclude that everything
  // on this screen is Prism-owned, which is exactly the wrong belief.
  const reloadReferences = useCallback(async () => {
    setReferencesLoading(true);
    setReferencesError(null);
    try {
      const res = await apiService.getPolicyReferences(customerId);
      setReferences(Array.isArray(res) ? res : []);
    } catch (e) {
      setReferences([]);
      setReferencesError(e?.message || 'Failed to load policy references');
    } finally {
      setReferencesLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    reloadReferences();
  }, [reloadReferences]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ name: '', description: '', policy_document: emptyDoc });
    setEditorError(null);
    setEditorOpen(true);
  };

  const openEdit = async (id) => {
    setEditingId(id);
    setEditorError(null);
    try {
      const full = await apiService.getCustomerManagedPolicy(id);
      setForm({
        name: full.name,
        description: full.description || '',
        policy_document: formatPolicyJson(full.policy_document),
      });
      setEditorOpen(true);
    } catch (e) {
      setError(e?.message || 'Failed to load policy');
    }
  };

  const submitEditor = async () => {
    setEditorError(null);
    setSaving(true);
    const body = {
      name: form.name,
      description: form.description,
      policy_document: form.policy_document,
    };
    try {
      if (editingId) {
        const usage = await apiService.getCustomerManagedPolicyUsage(editingId);
        if ((usage?.accounts || []).length > 0) {
          setPropagateConfirm({ usage, body });
          setSaving(false);
          return;
        }
        await apiService.updateCustomerManagedPolicy(editingId, body);
      } else {
        await apiService.createCustomerManagedPolicy(body);
      }
      setEditorOpen(false);
      reload();
    } catch (e) {
      setEditorError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const confirmPropagate = async () => {
    setSaving(true);
    try {
      // acknowledged: true is what the operator just supplied by confirming this
      // dialog. The server refuses an edit that rewrites the policy in already
      // provisioned accounts unless it is present (ErrBlastRadiusNotAcknowledged),
      // so re-sending the original body unchanged made the confirmation
      // decorative — every propagating edit failed with "was not acknowledged".
      await apiService.updateCustomerManagedPolicy(editingId, {
        ...propagateConfirm.body,
        acknowledged: true,
      });
      setPropagateConfirm(null);
      setEditorOpen(false);
      setEditorError(null);
      reload();
    } catch (e) {
      setEditorError(e?.message || 'Update failed');
      setPropagateConfirm(null);
    } finally {
      setSaving(false);
    }
  };

  const startDelete = async (row) => {
    setDeleteCandidate(row);
    setDeleteTyped('');
    try {
      const usage = await apiService.getCustomerManagedPolicyUsage(row.id);
      setDeleteUsage(usage);
    } catch {
      setDeleteUsage(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteCandidate) return;
    setDeleting(true);
    try {
      await apiService.deleteCustomerManagedPolicy(deleteCandidate.id);
      setDeleteCandidate(null);
      setDeleteUsage(null);
      setDeleteTyped('');
      reload();
    } catch (e) {
      setError(e?.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const collisionWarning = AWS_MANAGED_NAMES.has(form.name);

  const openCreateReference = () => {
    setRefForm({ name: '', path: '/' });
    setRefFormError(null);
    setRefCreateOpen(true);
  };

  const submitReference = async () => {
    setRefFormError(null);
    setRefSaving(true);
    try {
      await apiService.createPolicyReference(customerId, {
        name: refForm.name.trim(),
        path: refForm.path.trim() || '/',
      });
      setRefCreateOpen(false);
      reloadReferences();
    } catch (e) {
      setRefFormError(e?.message || 'Could not add the reference');
    } finally {
      setRefSaving(false);
    }
  };

  const confirmDeleteReference = async () => {
    if (!refDeleteCandidate) return;
    setRefDeleting(true);
    try {
      await apiService.deletePolicyReference(customerId, refDeleteCandidate.id);
      setRefDeleteCandidate(null);
      reloadReferences();
    } catch (e) {
      setReferencesError(e?.message || 'Could not remove the reference');
    } finally {
      setRefDeleting(false);
    }
  };

  // Leaving the detail panel always refreshes the list: the panel can re-check
  // the accounts, which is exactly what changes `variant_count`, and it exposes
  // no per-sweep callback to hook. Refreshing on the way out means the row an
  // admin returns to never contradicts the breakdown they just looked at.
  const closeRefDetail = useCallback(() => {
    setRefDetailId(null);
    reloadReferences();
  }, [reloadReferences]);

  // A converted reference has stopped being a reference and become a
  // Prism-managed policy, so BOTH lists move. The panel is deliberately left
  // open — it swaps itself for its own "now Prism-owned" confirmation, and that
  // screen's Done button is what closes it.
  const handleReferenceConverted = useCallback(() => {
    reloadReferences();
    reload();
  }, [reloadReferences, reload]);

  const q = search.trim().toLowerCase();
  const visibleReferences = q
    ? references.filter((r) => `${r.policy_name} ${r.policy_path}`.toLowerCase().includes(q))
    : references;

  // `variant_count` is the number of DISTINCT documents the sweep found, not the
  // number of accounts it looked at: 1 means every account agrees, 0 means Prism
  // has never found the policy anywhere (usually because it was never swept),
  // and only >1 is real divergence.
  const documentCountOf = (row) => row.variant_count || 0;

  const divergentReferences = visibleReferences.filter((r) => documentCountOf(r) > 1);
  const showEmptyState = rows.length === 0 && visibleReferences.length === 0;

  const renderDivergenceChip = (row) => {
    const count = documentCountOf(row);

    // Zero is an absence of information, not a finding. Colouring it as a
    // problem would cry wolf on every freshly added reference.
    if (count === 0) {
      return (
        <Tooltip title="Prism has not found this policy in any account yet. Open the reference and re-check the accounts to see what it resolves to.">
          <Chip size="small" label="Not yet checked" sx={statusChipSx('neutral')} />
        </Tooltip>
      );
    }

    if (count === 1) {
      return (
        <Tooltip title="Consistent — every account where Prism found this policy holds an identical document.">
          <Chip size="small" label="1 document" sx={statusChipSx('success')} />
        </Tooltip>
      );
    }

    return (
      <Tooltip title={`This policy is NOT the same everywhere: Prism found ${count} different documents across the customer's accounts. Open the reference to see each one and which accounts hold it.`}>
        <Chip
          size="small"
          icon={<WarningIcon sx={{ fontSize: 15 }} />}
          label={`${count} different documents`}
          sx={{
            ...statusChipSx('error'),
            height: 26,
            fontWeight: 700,
            border: '1px solid #E74C3C',
          }}
        />
      </Tooltip>
    );
  };

  // The reference detail takes the page over rather than opening in a dialog:
  // PolicyReferenceDetail is a full panel that owns its own convert and remove
  // dialogs, and nesting those inside another modal would stack them. The back
  // control lives here, not in the component, so there is still a way out when
  // the panel is loading or has failed to load and renders no close button.
  if (refDetailId) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate" style={PAGE_STYLE}>
        <Button
          onClick={closeRefDetail}
          startIcon={<BackIcon />}
          sx={{
            mb: 2,
            textTransform: 'none',
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 600,
            color: '#666666',
            '&:hover': { color: CMP_BRAND_COLOR, backgroundColor: 'transparent' },
          }}
        >
          Back to policies
        </Button>
        <PolicyReferenceDetail
          customerId={customerId}
          referenceId={refDetailId}
          onClose={closeRefDetail}
          onConverted={handleReferenceConverted}
          // The panel stays mounted over a deleted reference, so the page is
          // what unmounts it.
          onDeleted={closeRefDetail}
        />
      </motion.div>
    );
  }

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate" style={PAGE_STYLE}>
      {/* Page Header */}
      <motion.div variants={sectionVariants}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
            border: '1px solid #E5E5E5',
            p: 3,
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  backgroundColor: CMP_BRAND_COLOR,
                  width: 48,
                  height: 48,
                }}
              >
                <PolicyIcon sx={{ fontSize: 24 }} />
              </Avatar>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: 'Bitter, Georgia, serif',
                    fontWeight: 600,
                    fontSize: '1.75rem',
                    color: '#253E66',
                  }}
                >
                  Customer Managed Policies
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontSize: '0.875rem',
                    color: '#999999',
                    fontWeight: 500,
                  }}
                >
                  {total} Prism-managed {total === 1 ? 'policy' : 'policies'}
                  {' · '}
                  {references.length} {references.length === 1 ? 'reference' : 'references'} to
                  customer-owned policies
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search policies..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#999999', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: 250,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F9FBFD',
                    borderRadius: '6px',
                    fontFamily: 'Metropolis, sans-serif',
                    fontSize: '0.875rem',
                    '& fieldset': { borderColor: '#E5E5E5' },
                    '&:hover fieldset': { borderColor: CMP_BRAND_COLOR },
                    '&.Mui-focused fieldset': { borderColor: CMP_BRAND_COLOR },
                  },
                }}
              />
              <Tooltip title="Refresh">
                <IconButton
                  onClick={reload}
                  disabled={loading}
                  sx={{
                    color: '#666666',
                    backgroundColor: '#F9FBFD',
                    border: '1px solid #E5E5E5',
                    borderRadius: '6px',
                    '&:hover': {
                      backgroundColor: '#F0F4F8',
                      borderColor: CMP_BRAND_COLOR,
                      color: CMP_BRAND_COLOR,
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={20} sx={{ color: CMP_BRAND_COLOR }} />
                  ) : (
                    <RefreshIcon />
                  )}
                </IconButton>
              </Tooltip>
              <ReadOnlyButton
                variant="outlined"
                startIcon={<ReferenceIcon />}
                onClick={openCreateReference}
                disabled={isReadOnly}
                sx={{
                  color: REF_BRAND_COLOR,
                  borderColor: REF_BRAND_COLOR,
                  borderRadius: '6px',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 2.5,
                  '&:hover': { borderColor: REF_BRAND_COLOR, backgroundColor: '#EBF5FB' },
                }}
              >
                Add Reference
              </ReadOnlyButton>
              <ReadOnlyButton
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openCreate}
                disabled={isReadOnly}
                sx={{
                  backgroundColor: CMP_BRAND_COLOR,
                  borderRadius: '6px',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 3,
                  '&:hover': { backgroundColor: CMP_BRAND_HOVER },
                }}
              >
                New Policy
              </ReadOnlyButton>
            </Box>
          </Box>
        </Paper>
      </motion.div>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {referencesError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setReferencesError(null)}>
          {referencesError} — references to customer-owned policies are not shown below, so this
          list is incomplete.
        </Alert>
      )}

      {divergentReferences.length > 0 && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
          <strong>
            {divergentReferences.length}{' '}
            {divergentReferences.length === 1 ? 'reference resolves' : 'references resolve'} to
            different documents in different accounts.
          </strong>{' '}
          {divergentReferences.map((r) => r.policy_name).join(', ')} — the permissions granted
          through {divergentReferences.length === 1 ? 'it' : 'them'} are not the same in every
          account. Open the reference to see each version.
        </Alert>
      )}

      {/* Library List */}
      <motion.div variants={sectionVariants}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E5E5',
            borderRadius: '8px',
            overflow: 'hidden',
            minHeight: '400px',
          }}
        >
          {(loading || referencesLoading) && rows.length === 0 && references.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <CircularProgress sx={{ color: CMP_BRAND_COLOR }} />
            </Box>
          ) : showEmptyState ? (
            <Box sx={{ p: 8, textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  margin: '0 auto 16px',
                  backgroundColor: CMP_BRAND_TINT,
                }}
              >
                <PolicyIcon sx={{ fontSize: 32, color: CMP_BRAND_COLOR }} />
              </Avatar>
              <Typography
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  color: '#253E66',
                  mb: 1,
                }}
              >
                {search ? 'No matching policies' : 'No customer-managed policies yet'}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  color: '#666666',
                }}
              >
                {search
                  ? 'Try a different search term.'
                  : 'Create a policy to reuse the same document across permission sets, or add a reference to a policy the customer already owns.'}
              </Typography>
            </Box>
          ) : (
            <>
              <Table sx={{ '& .MuiTableCell-root': { borderColor: '#E5E5E5' } }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
                    <TableCell sx={HEAD_CELL_SX}>Type</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>Name</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>Description</TableCell>
                    <TableCell align="center" sx={HEAD_CELL_SX}>
                      <Tooltip title="Permission sets that attach this policy.">
                        <span>Permission sets</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" sx={HEAD_CELL_SX}>
                      <Tooltip title="How many distinct policy documents Prism has actually read across the customer's accounts. More than one means this policy is not the same everywhere.">
                        <span>Documents in accounts</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={HEAD_CELL_SX}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <motion.tbody variants={staggerContainer} initial="initial" animate="animate">
                  {/* References first: they are few, unpaginated by the API, and
                      the rows an admin most needs to spot before acting. */}
                  {visibleReferences.map((r) => (
                    <motion.tr key={`ref-${r.id}`} variants={staggerItem} style={{ display: 'table-row' }}>
                      <TableCell>
                        <Tooltip title={REFERENCE_OWNERSHIP_TEXT}>
                          <Chip
                            size="small"
                            icon={<ReferenceIcon sx={{ fontSize: 14 }} />}
                            label="Reference"
                            sx={statusChipSx('info')}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                        <Typography
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 600,
                            color: '#253E66',
                            fontSize: '0.95rem',
                          }}
                        >
                          {r.policy_name}
                        </Typography>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#999999' }}>
                          {r.policy_path || '/'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', color: '#666666', fontSize: '0.85rem' }}>
                        Owned by the customer — attached at its existing ARN, never modified by Prism.
                      </TableCell>
                      <TableCell align="center" sx={{ fontFamily: 'Metropolis, sans-serif', color: '#253E66', fontSize: '0.9rem', fontWeight: 600 }}>
                        {r.permission_set_count || 0}
                      </TableCell>
                      <TableCell align="center">{renderDivergenceChip(r)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="View reference detail, and convert it to a Prism-owned policy">
                          <IconButton
                            size="small"
                            onClick={() => setRefDetailId(r.id)}
                            sx={{ color: '#666666', '&:hover': { color: REF_BRAND_COLOR } }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {/* No edit action, by design: Prism does not own this
                            document and cannot change it. */}
                        <Tooltip title={isReadOnly ? "Read-only access — you don't have permission to make changes." : 'Remove reference (detach only)'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setRefDeleteCandidate(r)}
                              disabled={isReadOnly}
                              sx={{ color: '#666666', '&:hover': { color: '#E74C3C' } }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </motion.tr>
                  ))}
                  {rows.map((r) => (
                    <motion.tr
                      key={r.id}
                      variants={staggerItem}
                      style={{ display: 'table-row' }}
                    >
                      <TableCell>
                        <Tooltip title="Prism authors, owns and versions this policy, and materialises it as Prism-<Name> in every target account.">
                          <Chip
                            size="small"
                            icon={<PolicyIcon sx={{ fontSize: 14 }} />}
                            label="Prism-managed"
                            sx={statusChipSx('purple')}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Typography
                            sx={{
                              fontFamily: 'Metropolis, sans-serif',
                              fontWeight: 600,
                              color: '#253E66',
                              fontSize: '0.95rem',
                            }}
                          >
                            {r.name}
                          </Typography>
                          <Tooltip title="View rollout history">
                            <Chip
                              size="small"
                              variant="outlined"
                              icon={<TimelineIcon sx={{ fontSize: 14 }} />}
                              label="rollouts"
                              clickable
                              onClick={() => setRolloutPanel(r.id)}
                              sx={{
                                fontFamily: 'Metropolis, sans-serif',
                                fontSize: '0.7rem',
                                height: 22,
                                borderColor: '#E5E5E5',
                                color: '#666666',
                                '&:hover': {
                                  borderColor: CMP_BRAND_COLOR,
                                  color: CMP_BRAND_COLOR,
                                  backgroundColor: CMP_BRAND_TINT,
                                },
                              }}
                            />
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', color: '#666666', fontSize: '0.85rem' }}>
                        {r.description || '—'}
                      </TableCell>
                      <TableCell align="center" sx={{ color: '#999999' }}>—</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Tracked only for references. Prism owns this document and rolls the same version out to every account — see rollout history for what actually landed.">
                          <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#999999' }}>
                            —
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={isReadOnly ? "Read-only access — you don't have permission to make changes." : 'Edit'}>
                          <span>
                            <IconButton size="small" onClick={() => openEdit(r.id)} disabled={isReadOnly} sx={{ color: '#666666', '&:hover': { color: CMP_BRAND_COLOR } }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={isReadOnly ? "Read-only access — you don't have permission to make changes." : 'Delete'}>
                          <span>
                            <IconButton size="small" onClick={() => startDelete(r)} disabled={isReadOnly} sx={{ color: '#666666', '&:hover': { color: '#E74C3C' } }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </motion.tr>
                  ))}
                </motion.tbody>
              </Table>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                // Only the Prism-managed policies are paginated by the API; the
                // references above are the complete set every time, so the label
                // must not read as if it counted them too.
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} of ${count} Prism-managed policies`
                }
                sx={{
                  borderTop: '1px solid #E5E5E5',
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontFamily: 'Metropolis, sans-serif',
                  },
                }}
              />
            </>
          )}
        </Paper>
      </motion.div>

      {/* Editor */}
      <Dialog open={editorOpen} onClose={() => !saving && setEditorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66' }}>
          {editingId ? 'Edit' : 'New'} Customer Managed Policy
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              helperText={`Becomes "Prism-${form.name || '<name>'}" in each target account`}
              fullWidth
              autoFocus={!editingId}
            />
            {collisionWarning && (
              <Alert severity="warning">
                <strong>{form.name}</strong> matches an AWS-managed policy name. If you wanted to attach
                that AWS-managed policy, use the "AWS Managed Policies" section on the permission set instead.
              </Alert>
            )}
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth
            />
            <TextField
              label="Policy Document (JSON)"
              value={form.policy_document}
              onChange={(e) => setForm({ ...form, policy_document: e.target.value })}
              multiline
              minRows={14}
              maxRows={30}
              fullWidth
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
            />
            <PolicyGeneratorLink sx={{ mt: -1 }} />
            {editorError && <Alert severity="error">{editorError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditorOpen(false)} disabled={saving} sx={{ textTransform: 'none', fontFamily: 'Metropolis, sans-serif' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitEditor}
            disabled={saving || !form.name}
            sx={{
              backgroundColor: CMP_BRAND_COLOR,
              textTransform: 'none',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              px: 3,
              '&:hover': { backgroundColor: CMP_BRAND_HOVER },
            }}
          >
            {saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Propagation confirmation */}
      <Dialog open={!!propagateConfirm} onClose={() => !saving && setPropagateConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66' }}>
          Propagate update
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'Metropolis, sans-serif' }}>
            This will update the policy in{' '}
            <strong>{propagateConfirm?.usage?.accounts?.length || 0}</strong> account(s).
          </Typography>
          {propagateConfirm?.usage?.permission_sets?.length > 0 && (
            <Typography sx={{ mt: 1, fontFamily: 'Metropolis, sans-serif', color: '#666666', fontSize: '0.875rem' }}>
              Affected permission sets:{' '}
              {propagateConfirm.usage.permission_sets.map((p) => p.name).join(', ')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPropagateConfirm(null)} disabled={saving} sx={{ textTransform: 'none', fontFamily: 'Metropolis, sans-serif' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmPropagate}
            disabled={saving}
            sx={{
              backgroundColor: CMP_BRAND_COLOR,
              textTransform: 'none',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              '&:hover': { backgroundColor: CMP_BRAND_HOVER },
            }}
          >
            {saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Save and propagate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={!!deleteCandidate}
        onClose={() => !deleting && (setDeleteCandidate(null), setDeleteUsage(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66' }}>
          Delete customer-managed policy
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, fontFamily: 'Metropolis, sans-serif' }}>
            This will detach the policy from {deleteUsage?.role_attachments || 0} role(s) in{' '}
            {deleteUsage?.accounts?.length || 0} account(s) and delete the IAM policy from each.
            This cannot be undone.
          </Typography>
          <TextField
            fullWidth
            label={`Type "${deleteCandidate?.name || ''}" to confirm`}
            value={deleteTyped}
            onChange={(e) => setDeleteTyped(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => { setDeleteCandidate(null); setDeleteUsage(null); }}
            disabled={deleting}
            sx={{ textTransform: 'none', fontFamily: 'Metropolis, sans-serif' }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteTyped !== deleteCandidate?.name || deleting}
            onClick={confirmDelete}
            sx={{ textTransform: 'none', fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}
          >
            {deleting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add a reference to a customer-owned policy */}
      <Dialog open={refCreateOpen} onClose={() => !refSaving && setRefCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66' }}>
          Add policy reference
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Alert severity="info" icon={<ReferenceIcon />}>
              A reference points at a policy the <strong>customer</strong> already owns. Prism
              attaches it at its existing ARN and never creates, edits, versions or deletes the
              document — so the same name can resolve to a different document in each account.
            </Alert>
            <TextField
              label="Policy name"
              value={refForm.name}
              onChange={(e) => setRefForm({ ...refForm, name: e.target.value })}
              helperText="Must match the customer's IAM policy name exactly. Prism adds no prefix and creates nothing."
              fullWidth
              autoFocus
            />
            <TextField
              label="Path"
              value={refForm.path}
              onChange={(e) => setRefForm({ ...refForm, path: e.target.value })}
              helperText="The IAM path the policy lives under, e.g. / or /customer/."
              fullWidth
            />
            {refFormError && <Alert severity="error">{refFormError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setRefCreateOpen(false)}
            disabled={refSaving}
            sx={{ textTransform: 'none', fontFamily: 'Metropolis, sans-serif' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitReference}
            disabled={refSaving || !refForm.name.trim()}
            sx={{
              backgroundColor: REF_BRAND_COLOR,
              textTransform: 'none',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              px: 3,
              '&:hover': { backgroundColor: '#3580B8' },
            }}
          >
            {refSaving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Add reference'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove reference confirmation — detach only, and it must say so plainly */}
      <Dialog
        open={!!refDeleteCandidate}
        onClose={() => !refDeleting && setRefDeleteCandidate(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66' }}>
          Remove policy reference
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'Metropolis, sans-serif', mb: 1.5 }}>
            Prism will stop attaching <strong>{refDeleteCandidate?.policy_name}</strong>. Exactly
            this happens:
          </Typography>
          <Box component="ul" sx={{ pl: 3, m: 0, '& li': { mb: 1 } }}>
            <Typography component="li" sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.9rem', color: '#253E66' }}>
              It is detached from <strong>{refDeleteCandidate?.permission_set_count || 0}</strong>{' '}
              permission set(s). Anyone who got their access through those permission sets loses the
              permissions this policy granted.
            </Typography>
            <Typography component="li" sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.9rem', color: '#253E66' }}>
              Prism deletes its own pointer and everything it recorded about this policy, including
              the per-account documents it read.
            </Typography>
            <Typography component="li" sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.9rem', color: '#253E66' }}>
              The customer's IAM policy is <strong>not deleted</strong>.{' '}
              <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                {refDeleteCandidate ? `${refDeleteCandidate.policy_path || '/'}${refDeleteCandidate.policy_name}` : ''}
              </Box>{' '}
              stays in every AWS account exactly as it is, and anything outside Prism that attaches
              it keeps working.
            </Typography>
          </Box>
          <Typography sx={{ mt: 2, fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#666666' }}>
            You can add the reference again later; Prism would re-attach the same existing policy.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setRefDeleteCandidate(null)}
            disabled={refDeleting}
            sx={{ textTransform: 'none', fontFamily: 'Metropolis, sans-serif' }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDeleteReference}
            disabled={refDeleting}
            sx={{ textTransform: 'none', fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}
          >
            {refDeleting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Remove reference'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rollout status panel */}
      <AnimatePresence>
        {!!rolloutPanel && (
          <RolloutPanel policyId={rolloutPanel} onClose={() => setRolloutPanel(null)} />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function RolloutPanel({ policyId, onClose }) {
  const [rollouts, setRollouts] = useState([]);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!policyId) return;
    setLoading(true);
    try {
      const r = await apiService.listCustomerManagedPolicyRollouts(policyId);
      setRollouts(Array.isArray(r) ? r : []);
    } finally {
      setLoading(false);
    }
  }, [policyId]);

  useEffect(() => {
    if (!policyId) return undefined;
    reload();
    const t = setInterval(reload, 5000);
    return () => clearInterval(t);
  }, [policyId, reload]);

  const failed = rollouts.filter((r) => r.status === 'FAILED');
  const succeeded = rollouts.filter((r) => r.status === 'SUCCESS').length;
  const pending = rollouts.filter((r) => r.status === 'PENDING').length;

  const retry = async () => {
    try {
      await apiService.retryFailedCustomerManagedPolicyRollouts(policyId);
      reload();
    } catch { /* ignore */ }
  };

  const statusColor = (s) => {
    if (s === 'SUCCESS') return '#27AE60';
    if (s === 'FAILED') return '#E74C3C';
    if (s === 'PENDING') return '#F39C12';
    return '#666666';
  };

  return (
    <Dialog open={!!policyId} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66' }}>
        Rollout status
      </DialogTitle>
      <DialogContent>
        {/* Summary chips */}
        {rollouts.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Chip
              size="small"
              label={`${succeeded} success`}
              sx={{ backgroundColor: '#E8F5E9', color: '#27AE60', fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}
            />
            {pending > 0 && (
              <Chip
                size="small"
                label={`${pending} pending`}
                sx={{ backgroundColor: '#FFF3E0', color: '#F39C12', fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}
              />
            )}
            {failed.length > 0 && (
              <Chip
                size="small"
                label={`${failed.length} failed`}
                sx={{ backgroundColor: '#FFEBEE', color: '#E74C3C', fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}
              />
            )}
          </Box>
        )}

        {loading && rollouts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ color: CMP_BRAND_COLOR }} />
          </Box>
        ) : rollouts.length === 0 ? (
          <Typography sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif', textAlign: 'center', py: 4 }}>
            No rollouts recorded.
          </Typography>
        ) : (
          <Table size="small" sx={{ '& .MuiTableCell-root': { fontFamily: 'Metropolis, sans-serif' } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
                <TableCell sx={{ fontWeight: 600, color: '#253E66', fontSize: '0.75rem', textTransform: 'uppercase' }}>Account</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#253E66', fontSize: '0.75rem', textTransform: 'uppercase' }}>Event</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#253E66', fontSize: '0.75rem', textTransform: 'uppercase' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#253E66', fontSize: '0.75rem', textTransform: 'uppercase' }}>Error</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#253E66', fontSize: '0.75rem', textTransform: 'uppercase' }}>Started</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rollouts.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{r.account_id}</TableCell>
                  <TableCell>{r.event_type}</TableCell>
                  <TableCell sx={{ color: statusColor(r.status), fontWeight: 600 }}>{r.status}</TableCell>
                  <TableCell sx={{ color: '#E74C3C', fontSize: '0.8rem' }}>{r.error_message || ''}</TableCell>
                  <TableCell sx={{ fontSize: '0.85rem', color: '#666666' }}>{new Date(r.started_at).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {failed.length > 0 && (
          <Button
            onClick={retry}
            sx={{ textTransform: 'none', fontFamily: 'Metropolis, sans-serif', color: CMP_BRAND_COLOR }}
          >
            Retry {failed.length} failed
          </Button>
        )}
        <Button onClick={onClose} sx={{ textTransform: 'none', fontFamily: 'Metropolis, sans-serif' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
